/**
 * NotTrackedLead - an account made automatically for an opportunity
 * (crm_accounts.auto_created, Sharif 10/12/26: "keep their owners, but these
 * must not count as leads"). It is a Lead by designation, but the Leads
 * board, the Leads Dashboard and the "No next step" list leave it out.
 *
 *   <NotTrackedChip />    the subtle chip in the Accounts list and the header
 *   <NotTrackedNote />    the line on the Lead tab, with [Track as a lead]
 *
 * "Make Account" (Paul, 10/21/26) saves designation = Account: what these
 * records really are, now that Account is a designation of its own.
 * "Track as a lead" saves auto_created = false; a Stage or Status change does
 * the same on the server (lib/crm.applyAutoCreated). Both render nothing for
 * a record that is tracked, or that is a Customer (crmStages.isUntrackedLead).
 */
import { useState } from 'react';
import { isUntrackedLead } from '../../data/crmStages';
import { ERROR_LINE } from './crmStyles';

const WHY = 'Made automatically for an opportunity - not on the Leads board or the Leads Dashboard until someone tracks it as a lead';

export function NotTrackedChip({ account }) {
  if (!isUntrackedLead(account)) return null;
  return (
    <span
      title={WHY}
      style={{
        fontSize: 9.5,
        fontWeight: 700,
        color: '#616161',
        background: '#F5F5F5',
        border: '1px solid #E0E0E0',
        borderRadius: 10,
        padding: '1px 7px',
        whiteSpace: 'nowrap',
      }}
    >
      Not a lead
    </span>
  );
}

export function NotTrackedNote({ account, editable = false, onTrack, onMakeAccount }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!isUntrackedLead(account)) return null;
  const run = async (fn) => {
    if (busy || !fn) return;
    setBusy(true);
    setErr('');
    try {
      const r = await fn();
      if (r && r.ok === false) setErr(r.error || 'Could not save - try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      role="note"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        fontSize: 10.5,
        color: '#555',
        background: '#FAFAFA',
        border: '1px solid #EEEEEE',
        borderRadius: 8,
        padding: '8px 12px',
        marginBottom: 8,
      }}
    >
      <span style={{ flex: 1, minWidth: 200 }}>
        <b>Not tracked as a lead</b> - made automatically for an opportunity, so it is not on the Leads board.
      </span>
      {editable && onMakeAccount && (
        <button
          type="button"
          onClick={() => run(onMakeAccount)}
          disabled={busy}
          title="Designation Account: tracked, not a lead"
          style={{
            padding: '4px 10px',
            borderRadius: 6,
            border: '1px solid #455A64',
            background: '#455A64',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            cursor: busy ? 'default' : 'pointer',
            fontFamily: 'Montserrat',
          }}
        >
          Make Account
        </button>
      )}
      {editable && onTrack && (
        <button
          type="button"
          onClick={() => run(onTrack)}
          disabled={busy}
          style={{
            padding: '4px 10px',
            borderRadius: 6,
            border: '1px solid #1565C0',
            background: '#fff',
            color: '#1565C0',
            fontSize: 10,
            fontWeight: 700,
            cursor: busy ? 'default' : 'pointer',
            fontFamily: 'Montserrat',
          }}
        >
          {busy ? 'Saving...' : 'Track as a lead'}
        </button>
      )}
      {err && <div style={{ ...ERROR_LINE, flexBasis: '100%' }}>{err}</div>}
    </div>
  );
}
